import { rsaEncrypt } from '@fx-ui/platform-sdk';
import CryptoJS from 'crypto-js';

/**
 * RSA + AES 加密
 *
 * @description
 *  AES 本身安全性不够强，而 RSA 加密的性能较差。
 *  因此使用 AES 对大量数据进行高性能加密，同时使用 RSA 加密 AES 密钥，以提高安全性。
 */
export class RsaAesHybridEncrypt {
    private encryptedKey?: string;
    private encryptedIv?: string;
    /** aes的key和iv是否加密成功 */
    private isRsaEncryptionSucceeded = false;

    private readonly aesKey: CryptoJS.lib.WordArray;
    private readonly iv: CryptoJS.lib.WordArray;

    constructor() {
        // 随机生成 AES 密钥和 IV
        this.aesKey = CryptoJS.lib.WordArray.random(32);
        this.iv = CryptoJS.lib.WordArray.random(16);
    }

    /**
     * 加密数据
     *
     * @returns 加密后的数据。如果加密失败，则会返回原数据
     */
    encrypt(data: string) {
        // 每次加密前都进行检查
        this.setupKeyEncryption();
        if (!this.isRsaEncryptionSucceeded) {
            return data;
        }
        const aesKey = this.aesKey;
        const iv = this.iv;

        // 使用 AES 加密数据
        const encryptedData = CryptoJS.AES.encrypt(data, aesKey, {
            iv,
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7,
            format: CryptoJS.format.OpenSSL
        }).toString();

        return encryptedData;
    }

    /**
     * 获取加密配置，发起请求时携带
     *
     * @returns 加密后的aes配置。如果加密失败，则会返回空对象
     */
    getOptions = () => {
        if (!this.isRsaEncryptionSucceeded) {
            return {};
        }
        return {
            key: this.encryptedKey,
            iv: this.encryptedIv
        };
    };

    /**
     * 设置AES Key和IV的加密状态
     *
     * @description rsa的加密依赖与public_configuration的返回，因此不能在构造时就初始化
     */
    private setupKeyEncryption = () => {
        // key已经加密，则直接返回
        if (this.isRsaEncryptionSucceeded) {
            return;
        }
        const aesKeyString = this.aesKey.toString();
        const ivString = this.iv.toString();

        // 使用 RSA 加密 AES 密钥
        const encryptedKey = rsaEncrypt(aesKeyString);

        // 使用 RSA 加密 IV
        const encryptedIv = rsaEncrypt(ivString);

        // 加密前后相等，视作加密失败
        if (encryptedKey !== aesKeyString && encryptedIv !== ivString) {
            this.encryptedKey = encryptedKey;
            this.encryptedIv = encryptedIv;
            this.isRsaEncryptionSucceeded = true;
        } else {
            this.isRsaEncryptionSucceeded = false;
        }
    };
}

/** rsa+aes混合加密器 */
export const hybridEncryptor = new RsaAesHybridEncrypt();

/**
 * 获取加密后的密码
 *
 * @param password 密码
 */
export const getEncryptedPassword = (password: string) => {
    return rsaEncrypt(password);
};
